import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Avatar,
  Alert,
  LinearProgress,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import EmailIcon from "@mui/icons-material/Email";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";

import { useAuthState } from "react-firebase-hooks/auth";
import { sendSignInLinkToEmail, updateProfile } from "firebase/auth";
import { doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

import { auth, db, storage } from "../../../components/firebase";
import { sanitizeText, sanitizeEmail } from "../../../lib/sanitize";

const PROJECT = process.env.REACT_APP_FIREBASE_PROJECT_ID;
const REGION = "europe-west3";
const CF_BASE = process.env.REACT_APP_CF_BASE || `https://${REGION}-${PROJECT}.cloudfunctions.net`;

const R12 = "12px";
const GLASS = {
  surface: "linear-gradient(180deg, rgba(12,16,22,.92) 0%, rgba(8,11,17,.92) 60%)",
  border: "1px solid rgba(255,255,255,.08)",
  borderStrong: "1px solid rgba(255,255,255,.16)",
  shadow: "0 10px 32px rgba(2,8,23,.45)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const Section = styled(Card)({
  borderRadius: R12,
  background: GLASS.surface,
  border: GLASS.border,
  boxShadow: GLASS.shadow,
  color: GLASS.text,
});

const Row = ({ title, desc, children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.2fr" }, gap: 2, py: 2 }}>
    <Box>
      <Typography sx={{ fontWeight: 950 }}>{title}</Typography>
      {desc ? <Typography sx={{ color: GLASS.subtext, fontSize: 12, mt: 0.5 }}>{desc}</Typography> : null}
    </Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

function isWalletOnly(userDoc) {
  const ap = userDoc?.authProviders || {};
  return ap?.wallet === true && ap?.email !== true;
}

function normalizeNickname(s) {
  const n = String(s || "").trim();
  const cleaned = n.replace(/\s+/g, " ");
  return cleaned.slice(0, 24);
}

function validateNickname(n) {
  if (!n) return "Nickname is required.";
  if (n.length < 3) return "Min 3 characters.";
  if (n.length > 24) return "Max 24 characters.";
  if (!/^[a-zA-Z0-9 _.-]+$/.test(n)) return "Only letters, digits, space, _ . -";
  return "";
}

function safeExt(name = "", fallback = "jpg") {
  const m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return m?.[1] || fallback;
}

/** ===== Email attach helpers (NEW) ===== */
const ATTACH_KEY = "pp_emailAttach_email";

function getRedirectBase() {
  const forced = process.env.REACT_APP_EMAIL_REDIRECT_BASE;
  return forced ? forced.replace(/\/+$/, "") : window.location.origin;
}

async function sendAttachLink(em) {
  if (!auth.currentUser) throw new Error("Not authenticated");

  // 1) create attachToken on backend (wallet user is logged in)
  const walletIdToken = await auth.currentUser.getIdToken(true);

  const r = await fetch(`${CF_BASE}/createEmailAttach`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${walletIdToken}`,
    },
    body: JSON.stringify({ email: em }),
  });

  // IMPORTANT: read as JSON first (your backend should return {attachToken})
  const data = await r.json().catch(() => null);
  if (!r.ok || !data?.attachToken) {
    // fallback to text error for debugging
    const text = await r.text().catch(() => "");
    throw new Error(text || "createEmailAttach failed");
  }

  // 2) send Firebase email-link (Firebase templates)
  const base = getRedirectBase();
  const actionCodeSettings = {
  url: `${base}/finish-email-attach?token=${encodeURIComponent(data.attachToken)}&email=${encodeURIComponent(em)}`,
  handleCodeInApp: true,
};

  await sendSignInLinkToEmail(auth, em, actionCodeSettings);

  // so the device that opens the email can auto-fill; otherwise Finish page asks manually
  window.localStorage.setItem(ATTACH_KEY, em);
}

export default function SettingsPanel() {
  const [user] = useAuthState(auth);

  const [userDoc, setUserDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(true);

  // Nickname UX
  const [nickEditing, setNickEditing] = useState(false);
  const [nickname, setNickname] = useState("");
  const [savingNick, setSavingNick] = useState(false);
  const [nickMsg, setNickMsg] = useState(null);

  // Email UX
  const [emailEditing, setEmailEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);

  // Avatar
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarProgress, setAvatarProgress] = useState(0);
  const [avatarMsg, setAvatarMsg] = useState(null);

  useEffect(() => {
    if (!user?.uid) return;

    const refDoc = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      refDoc,
      (snap) => {
        const d = snap.exists() ? snap.data() : null;
        setUserDoc(d);
        setNickname(String(d?.nickname || ""));
        setLoadingDoc(false);
      },
      () => setLoadingDoc(false)
    );

    return () => unsub();
  }, [user?.uid]);

  const needEmail = useMemo(() => isWalletOnly(userDoc), [userDoc]);
  const originalNick = useMemo(() => normalizeNickname(userDoc?.nickname || ""), [userDoc?.nickname]);
  const nickNormalized = useMemo(() => normalizeNickname(nickname), [nickname]);
  const nickErr = useMemo(() => validateNickname(nickNormalized), [nickNormalized]);
  const nickChanged = useMemo(() => nickNormalized !== originalNick, [nickNormalized, originalNick]);
  const hasNick = Boolean(originalNick);

  const linkedEmail = String(userDoc?.email || "").trim();
  const emailPending = String(userDoc?.emailPending || "").trim();
  const emailVerified = Boolean(userDoc?.emailVerified);

  // show email input if:
  // - no email yet
  // - OR user clicks Change
  const showEmailInput = useMemo(() => {
    if (!linkedEmail) return true;
    return emailEditing;
  }, [linkedEmail, emailEditing]);

  const onStartCreateNickname = () => {
    setNickMsg(null);
    setNickEditing(true);
  };

  const onStartEditNickname = () => {
    setNickMsg(null);
    setNickEditing(true);
  };

  const onCancelEditNickname = () => {
    setNickMsg(null);
    setNickEditing(false);
    setNickname(String(userDoc?.nickname || ""));
  };

  const onSaveNickname = async () => {
    if (!user?.uid) return;

    const n = normalizeNickname(sanitizeText(nickname, 24));

    const err = validateNickname(n);
    if (err) {
      setNickMsg({ type: "error", text: err });
      return;
    }

    setSavingNick(true);
    setNickMsg(null);

    try {
      await updateDoc(doc(db, "users", user.uid), {
        nickname: n,
        updatedAt: serverTimestamp(),
      });

      setNickMsg({ type: "success", text: "Saved." });
      setNickEditing(false);
    } catch (e) {
      setNickMsg({ type: "error", text: e?.message || "Failed to save nickname." });
    } finally {
      setSavingNick(false);
    }
  };

  /** ===== NEW: send attach link (replaces old onSendEmailLink) ===== */
  const onSendEmailLink = async () => {
    if (!user?.uid) return;

    const em = sanitizeEmail(email);

    if (!em || !em.includes("@")) {
      setEmailMsg({ type: "error", text: "Enter a valid email." });
      return;
    }

    setEmailBusy(true);
    setEmailMsg(null);

    try {
      await sendAttachLink(em);
      setEmailMsg({ type: "success", text: "We sent a confirmation link. Open it to verify email." });
      setEmail("");
    } catch (e) {
      setEmailMsg({ type: "error", text: e?.message || "Failed to send email link." });
    } finally {
      setEmailBusy(false);
    }
  };

  /** ===== NEW: resend pending using same flow ===== */
  const onResendPending = async () => {
    if (!user?.uid) return;
    if (!emailPending) {
      setEmailMsg({ type: "error", text: "No pending email found." });
      return;
    }

    setEmailBusy(true);
    setEmailMsg(null);

    try {
      await sendAttachLink(sanitizeEmail(emailPending));

      setEmailMsg({ type: "success", text: "Verification link re-sent." });
    } catch (e) {
      setEmailMsg({ type: "error", text: e?.message || "Failed to resend link." });
    } finally {
      setEmailBusy(false);
    }
  };

  const onPickAvatar = async (file) => {
    if (!user?.uid || !file) return;

    setAvatarBusy(true);
    setAvatarMsg(null);
    setAvatarProgress(0);

    try {
      if (!file.type.startsWith("image/")) throw new Error("Only image files allowed.");
      if (file.size > 2 * 1024 * 1024) throw new Error("Max file size: 2MB.");

      const ext = safeExt(file.name, "jpg");
      const path = `avatars/${user.uid}/avatar.${ext}`;
      const r = ref(storage, path);

      const task = uploadBytesResumable(r, file, {
        contentType: file.type || undefined,
        cacheControl: "public,max-age=31536000,immutable",
      });

      const url = await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const p = snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0;
            setAvatarProgress(Math.round(p));
          },
          (err) => reject(err),
          async () => resolve(await getDownloadURL(task.snapshot.ref))
        );
      });

      await updateDoc(doc(db, "users", user.uid), {
        avatarUrl: url,
        updatedAt: serverTimestamp(),
      });

      try {
        await updateProfile(auth.currentUser, { photoURL: url });
      } catch {}

      setAvatarMsg({ type: "success", text: "Avatar updated." });
    } catch (e) {
      setAvatarMsg({ type: "error", text: e?.message || "Failed to upload avatar." });
    } finally {
      setAvatarBusy(false);
    }
  };

  if (!user) {
    return (
      <Section sx={{ p: 2 }}>
        <Typography sx={{ color: GLASS.text, fontWeight: 900 }}>Not authenticated</Typography>
      </Section>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Section sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Settings</Typography>
        <Typography sx={{ color: GLASS.subtext, fontSize: 12, mt: 0.5 }}>
          Manage your profile, security and recovery options.
        </Typography>

        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.08)" }} />

        {loadingDoc ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography sx={{ color: GLASS.subtext, fontWeight: 800 }}>Loading…</Typography>
          </Box>
        ) : (
          <>
            {/* ===== Nickname (create -> edit) ===== */}
            <Row title="Nickname" desc="Public name shown in profile and leaderboards.">
              {!hasNick && !nickEditing ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Alert severity="info" sx={{ flex: 1, bgcolor: "rgba(0,0,0,.2)", color: GLASS.text }}>
                    You don’t have a nickname yet. Create it to display your profile name.
                  </Alert>

                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={onStartCreateNickname}
                    sx={{
                      textTransform: "none",
                      fontWeight: 950,
                      borderRadius: 2,
                      px: 2,
                      whiteSpace: "nowrap",
                      background:
                        "linear-gradient(135deg, rgba(56,189,248,1) 0%, rgba(59,130,246,1) 55%, rgba(99,102,241,1) 120%)",
                      boxShadow: "0 12px 30px rgba(56,189,248,.18)",
                    }}
                  >
                    Create
                  </Button>
                </Box>
              ) : !nickEditing ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 1000, color: GLASS.text, fontSize: 14 }}>
                      {originalNick}
                    </Typography>
                    <Typography sx={{ color: GLASS.subtext, fontSize: 12, mt: 0.25 }}>
                      This name will be visible in header and leaderboards.
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={onStartEditNickname}
                    sx={{
                      textTransform: "none",
                      fontWeight: 950,
                      borderRadius: 2,
                      px: 2,
                      whiteSpace: "nowrap",
                      borderColor: "rgba(255,255,255,.16)",
                      color: GLASS.text,
                    }}
                  >
                    Change
                  </Button>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      value={nickname}
                      onChange={(e) => {
  setNickMsg(null);
  setNickname(sanitizeText(e.target.value, 24)); 
}}

                      size="small"
                      fullWidth
                      placeholder="Your nickname"
                      error={!!nickErr && nickChanged}
                      helperText={nickChanged && nickErr ? nickErr : " "}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "rgba(15,23,42,.45)",
                          color: "#e5e7eb",
                          "& fieldset": { borderColor: "rgba(148,163,184,.25)" },
                          "&:hover fieldset": { borderColor: "rgba(56,189,248,.35)" },
                          "&.Mui-focused fieldset": { borderColor: "rgba(56,189,248,.55)" },
                        },
                        "& .MuiFormHelperText-root": { color: "rgba(148,163,184,.9)" },
                      }}
                    />

                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={savingNick || !nickChanged || !!nickErr}
                      onClick={onSaveNickname}
                      sx={{
                        textTransform: "none",
                        fontWeight: 950,
                        borderRadius: 2,
                        px: 2,
                        whiteSpace: "nowrap",
                        background:
                          "linear-gradient(135deg, rgba(56,189,248,1) 0%, rgba(59,130,246,1) 55%, rgba(99,102,241,1) 120%)",
                        boxShadow: "0 12px 30px rgba(56,189,248,.18)",
                      }}
                    >
                      {savingNick ? "Saving…" : "Save"}
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      disabled={savingNick}
                      onClick={onCancelEditNickname}
                      sx={{
                        textTransform: "none",
                        fontWeight: 950,
                        borderRadius: 2,
                        px: 2,
                        whiteSpace: "nowrap",
                        borderColor: "rgba(255,255,255,.16)",
                        color: GLASS.text,
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>

                  {nickMsg ? (
                    <Alert severity={nickMsg.type} sx={{ mt: 1, bgcolor: "rgba(0,0,0,.2)", color: GLASS.text }}>
                      {nickMsg.text}
                    </Alert>
                  ) : null}
                </>
              )}
            </Row>

            <Divider sx={{ borderColor: "rgba(255,255,255,.08)" }} />

            {/* ===== Avatar ===== */}
            <Row title="Avatar" desc="Upload an avatar image (max 2MB).">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  src={userDoc?.avatarUrl || auth.currentUser?.photoURL || ""}
                  sx={{ width: 44, height: 44, border: "1px solid rgba(255,255,255,.12)" }}
                />

                <Button
                  component="label"
                  startIcon={<PhotoCameraIcon />}
                  disabled={avatarBusy}
                  sx={{
                    textTransform: "none",
                    fontWeight: 900,
                    borderRadius: 2,
                    border: "1px solid rgba(255,255,255,.14)",
                    color: GLASS.text,
                  }}
                >
                  {avatarBusy ? "Uploading…" : "Upload"}
                  <input hidden type="file" accept="image/*" onChange={(e) => onPickAvatar(e.target.files?.[0])} />
                </Button>
              </Box>

              {avatarBusy ? (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant={avatarProgress > 0 ? "determinate" : "indeterminate"}
                    value={avatarProgress}
                    sx={{ borderRadius: 2 }}
                  />
                  <Typography sx={{ mt: 0.6, color: GLASS.subtext, fontSize: 12 }}>
                    Uploading: {avatarProgress || 0}%
                  </Typography>
                </Box>
              ) : null}

              {avatarMsg ? (
                <Alert severity={avatarMsg.type} sx={{ mt: 1, bgcolor: "rgba(0,0,0,.2)", color: GLASS.text }}>
                  {avatarMsg.text}
                </Alert>
              ) : null}
            </Row>

            <Divider sx={{ borderColor: "rgba(255,255,255,.08)" }} />

            {/* ===== Email ===== */}
            <Row title="Email" desc="Add and verify email via link (recovery & security).">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  {linkedEmail ? (
                    <>
                      <Chip
                        size="small"
                        label={emailVerified ? "verified" : "not verified"}
                        sx={{
                          height: 22,
                          borderRadius: 1,
                          fontWeight: 900,
                          bgcolor: emailVerified ? "rgba(20,241,149,.14)" : "rgba(248,113,113,.14)",
                          color: GLASS.text,
                          border: "1px solid rgba(255,255,255,.12)",
                          "& .MuiChip-label": { px: 0.75, fontSize: 11, lineHeight: "22px", fontWeight: 900 },
                        }}
                      />
                      <Typography sx={{ color: GLASS.text, fontWeight: 900 }}>{linkedEmail}</Typography>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setEmailEditing((v) => !v)}
                        sx={{
                          ml: "auto",
                          textTransform: "none",
                          fontWeight: 950,
                          borderRadius: 2,
                          borderColor: "rgba(255,255,255,.16)",
                          color: GLASS.text,
                        }}
                      >
                        Change
                      </Button>
                    </>
                  ) : (
                    <Alert severity={needEmail ? "warning" : "info"} sx={{ flex: 1, bgcolor: "rgba(0,0,0,.2)", color: GLASS.text }}>
                      {needEmail
                        ? "Wallet account: add email to enable recovery and verification."
                        : "You can add email for recovery and security."}
                    </Alert>
                  )}
                </Box>

                {emailPending ? (
                  <Alert severity="info" sx={{ bgcolor: "rgba(0,0,0,.2)", color: GLASS.text }}>
                    Pending verification: <b>{emailPending}</b>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        startIcon={<ReplayIcon />}
                        disabled={emailBusy}
                        onClick={onResendPending}
                        sx={{
                          textTransform: "none",
                          fontWeight: 950,
                          borderRadius: 2,
                          border: "1px solid rgba(255,255,255,.14)",
                          color: GLASS.text,
                        }}
                      >
                        {emailBusy ? "Sending…" : "Resend link"}
                      </Button>
                    </Box>
                  </Alert>
                ) : null}

                {showEmailInput ? (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      value={email}
                      onChange={(e) => setEmail(sanitizeEmail(e.target.value))}

                      size="small"
                      fullWidth
                      placeholder="your@email.com"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "rgba(15,23,42,.45)",
                          color: "#e5e7eb",
                          "& fieldset": { borderColor: "rgba(148,163,184,.25)" },
                          "&:hover fieldset": { borderColor: "rgba(56,189,248,.35)" },
                          "&.Mui-focused fieldset": { borderColor: "rgba(56,189,248,.55)" },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<EmailIcon />}
                      disabled={emailBusy}
                      onClick={onSendEmailLink}
                      sx={{
                        textTransform: "none",
                        fontWeight: 950,
                        borderRadius: 2,
                        px: 2,
                        whiteSpace: "nowrap",
                        background:
                          "linear-gradient(135deg, rgba(34,197,94,1) 0%, rgba(16,185,129,1) 60%, rgba(59,130,246,1) 140%)",
                        boxShadow: "0 12px 30px rgba(34,197,94,.12)",
                      }}
                    >
                      {emailBusy ? "Sending…" : "Send link"}
                    </Button>

                    {linkedEmail ? (
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        disabled={emailBusy}
                        onClick={() => {
                          setEmailEditing(false);
                          setEmail("");
                          setEmailMsg(null);
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 950,
                          borderRadius: 2,
                          px: 2,
                          whiteSpace: "nowrap",
                          borderColor: "rgba(255,255,255,.16)",
                          color: GLASS.text,
                        }}
                      >
                        Close
                      </Button>
                    ) : null}
                  </Box>
                ) : null}

                {emailMsg ? (
                  <Alert severity={emailMsg.type} sx={{ bgcolor: "rgba(0,0,0,.2)", color: GLASS.text }}>
                    {emailMsg.text}
                  </Alert>
                ) : null}
              </Box>
            </Row>
          </>
        )}
      </Section>
    </Box>
  );
}
